import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Home as HomeIcon,
  Check as CheckIcon,
  Star as StarIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';

interface PricingTier {
  name: string;
  price: string;
  period?: string;
  description: string;
  features: string[];
  icon: React.ReactNode;
  color: string;
  popular?: boolean;
  buttonText: string;
  deliveryTime?: string;
}

const pricingTiers: PricingTier[] = [
  {
    name: 'Basic Analysis',
    price: '$20',
    period: 'per analysis',
    description: 'Perfect for individual property analysis in our covered areas',
    features: [
      'Complete feasibility analysis',
      'Property assessment data',
      'Approximate floor plans',
      'Zoning compliance check',
      'Basic financial projections',
      'Development scenarios',
      'Export to PDF',
      'Available in: Somerville, Cambridge, Lexington',
    ],
    icon: <TrendingUpIcon />,
    color: 'primary.main',
    buttonText: 'Analyze Property',
  },
  {
    name: 'Pro Subscription',
    price: '$80',
    period: 'per month',
    description: 'Unlimited access for professionals in our covered areas',
    features: [
      'Unlimited property searches',
      'All Basic features included',
      'Advanced financial modeling',
      'Market comparison analysis',
      'Save and compare properties',
      'Priority support',
      'API access (coming soon)',
      'Team collaboration tools',
      'Available in: Somerville, Cambridge, Lexington',
    ],
    icon: <StarIcon />,
    color: 'secondary.main',
    popular: true,
    buttonText: 'Start Free Trial',
  },
  {
    name: 'Enterprise Study',
    price: '$3,000',
    description: 'Official architectural feasibility study - AVAILABLE NATIONWIDE',
    features: [
      'Stamped architectural plans',
      'Official floor plans & renderings',
      'Professional 3D visualizations',
      'Detailed engineering analysis',
      'Permit-ready documentation',
      'Dedicated project manager',
      'Revisions included',
      'Express delivery available',
      '🌟 Available in ALL towns, counties & states',
    ],
    icon: <BusinessIcon />,
    color: 'success.main',
    buttonText: 'Call 781-528-3339',
    deliveryTime: 'Delivered within 2-3 business days',
  },
];

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const handlePricingAction = (tier: PricingTier) => {
    if (tier.name === 'Enterprise Study') {
      navigate('/contact');
    } else if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/register');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Navigation */}
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar>
          <IconButton edge="start" color="primary" sx={{ mr: 2 }} onClick={() => navigate('/')}>
            <HomeIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, color: 'primary.main', fontWeight: 600 }}>
            QuickBuildr.ai
          </Typography>
          {isAuthenticated ? (
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </Button>
          ) : (
            <>
              <Button
                color="primary"
                sx={{ mr: 2 }}
                onClick={() => navigate('/login')}
              >
                Sign In
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/register')}
              >
                Get Started
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Container maxWidth="lg">
        <Box sx={{ py: { xs: 6, md: 10 }, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              variant="h2"
              fontWeight={700}
              color="primary.main"
              gutterBottom
            >
              Simple, Transparent Pricing
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 800, mx: 'auto' }}
            >
              Choose the perfect plan for your real estate analysis needs
            </Typography>
            
            {/* Coverage Notice */}
            <Box sx={{ mb: 6, p: 2, bgcolor: 'info.main', color: 'white', borderRadius: 2, maxWidth: 900, mx: 'auto' }}>
              <Typography variant="body1" fontWeight={600}>
                📍 AI Analysis currently available in: Somerville, Cambridge, and Lexington MA
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Professional Architectural Studies available nationwide • Call 781-528-3339
              </Typography>
            </Box>
          </motion.div>

          {/* Pricing Cards */}
          <Grid container spacing={4} alignItems="stretch">
            {pricingTiers.map((tier, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  style={{ height: '100%' }}
                >
                  <Card
                    elevation={tier.popular ? 8 : 2}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      border: tier.popular ? `2px solid ${tier.color}` : '1px solid #e0e0e0',
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                      },
                    }}
                  >
                    {tier.popular && (
                      <Chip
                        label="MOST POPULAR"
                        color="secondary"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -12,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontWeight: 600,
                        }}
                      />
                    )}

                    <CardContent sx={{ flexGrow: 1, pt: 4 }}>
                      <Box sx={{ color: tier.color, mb: 2 }}>
                        {tier.icon}
                      </Box>
                      
                      <Typography variant="h5" fontWeight={600} gutterBottom>
                        {tier.name}
                      </Typography>
                      
                      <Box sx={{ my: 3 }}>
                        <Typography variant="h3" fontWeight={700} color={tier.color}>
                          {tier.price}
                        </Typography>
                        {tier.period && (
                          <Typography variant="body2" color="text.secondary">
                            {tier.period}
                          </Typography>
                        )}
                        {tier.name === 'Enterprise Study' && (
                          <Chip
                            label="NATIONWIDE"
                            color="success"
                            size="small"
                            sx={{ mt: 1, fontWeight: 600 }}
                          />
                        )}
                      </Box>

                      <Typography variant="body1" color="text.secondary" paragraph>
                        {tier.description}
                      </Typography>

                      {tier.deliveryTime && (
                        <Chip
                          label={tier.deliveryTime}
                          color="success"
                          variant="outlined"
                          size="small"
                          sx={{ mb: 2 }}
                        />
                      )}

                      <List dense>
                        {tier.features.map((feature, idx) => (
                          <ListItem key={idx} disableGutters>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <CheckIcon color="success" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={feature}
                              primaryTypographyProps={{
                                variant: 'body2',
                                color: 'text.primary',
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>

                    <CardActions sx={{ p: 3, pt: 0 }}>
                      <Button
                        fullWidth
                        variant={tier.popular ? 'contained' : 'outlined'}
                        color="primary"
                        size="large"
                        onClick={() => handlePricingAction(tier)}
                        sx={{
                          py: 1.5,
                          fontWeight: 600,
                          bgcolor: tier.popular ? tier.color : 'transparent',
                        }}
                      >
                        {tier.buttonText}
                      </Button>
                    </CardActions>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* FAQ Section */}
          <Box sx={{ mt: 10 }}>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              Frequently Asked Questions
            </Typography>
            
            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="h6" gutterBottom>
                    What's included in the Basic Analysis?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Each analysis includes comprehensive property data, zoning compliance verification,
                    development scenarios (renovation, extension, teardown), approximate floor plans,
                    and financial projections with ROI calculations.
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="h6" gutterBottom>
                    How quickly do I get the Enterprise Study?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Our stamped architectural studies are delivered within 2-3 business days (48 business hours).
                    This includes professional floor plans, renderings, and all permit-ready documentation.
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="h6" gutterBottom>
                    Can I cancel my Pro subscription anytime?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Yes, you can cancel your Pro subscription at any time. You'll continue to have access
                    until the end of your billing period, and no further charges will be made.
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="h6" gutterBottom>
                    Do you offer team or bulk discounts?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Yes! We offer custom pricing for teams and enterprises. Contact our sales team
                    for a personalized quote that fits your organization's needs.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* CTA Section */}
          <Box sx={{ mt: 10, p: 6, bgcolor: 'primary.main', borderRadius: 2, color: 'white' }}>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              Ready to analyze your first property?
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Start with our free trial and see the power of data-driven real estate analysis.
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'grey.100',
                },
              }}
              onClick={() => navigate('/register')}
            >
              Start Free Trial
            </Button>
          </Box>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: 'primary.dark', color: 'white', py: 4, mt: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" textAlign="center" sx={{ opacity: 0.8 }}>
            © 2025 QuickBuildr.ai. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default PricingPage;